import {XIcon} from '@heroicons/react/solid';
import type {ChangeEvent} from 'react';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, DatePicker, Input, SearchBar, Select} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {Filter} from '@/components/Filter';

import DownloadTradesCsvButton from './DownloadTradesCsvButton';
import type {TradesFiltersProps} from './types';
import {PAIR_PATTERN, TRADE_STATUS_OPTIONS} from './utils/format';

const toDate = (value?: string | Date): Date | undefined =>
  value ? new Date(value) : undefined;

const TradesFilters = ({
  filtersToApply,
  appliedFilters,
  askId,
  selectAppliedFilters,
  handleApplyFilters,
  clearFilters,
  clearAskId,
  handleSearch,
}: TradesFiltersProps) => {
  const [showFilters, setShowFilters] = useState(false);

  const pair = filtersToApply.pair ?? '';
  const pairError =
    pair && !PAIR_PATTERN.test(pair)
      ? 'Use a pair like USD-NGN (3-5 letters each side).'
      : undefined;

  // Apply is disabled while the pair is malformed, so this never sees one.
  const handleApply = () => {
    // Only close on success: a rejected date range must stay visible so the
    // user can correct it.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const hasDateRange = Boolean(
    appliedFilters.fromDate || appliedFilters.toDate,
  );
  // Same test as the server's resolveTradeFilters: a longer number cannot be
  // a bid id, so it is searched as a name, under the default lookback.
  const search = appliedFilters.search ?? '';
  const searchesById =
    /^\d+$/.test(search) && Number.isSafeInteger(Number(search));
  const usesDefaultLookback = !hasDateRange && !askId && !searchesById;

  return (
    <div className="mt-5 w-full space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={() => setShowFilters((current) => !current)}
            label="Filters"
            positionLeft={1}
            bodyClassName="w-full md:w-[460px]"
          >
            {showFilters && (
              <div className="col-span-full w-full space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DatePicker
                    label="Placed from"
                    selectedDate={toDate(filtersToApply.fromDate)}
                    setSelectedDate={(date) =>
                      selectAppliedFilters('fromDate', date)
                    }
                    placeholderText="Select date"
                  />
                  <DatePicker
                    label="Placed to"
                    selectedDate={toDate(filtersToApply.toDate)}
                    setSelectedDate={(date) =>
                      selectAppliedFilters('toDate', date)
                    }
                    placeholderText="Select date"
                  />
                </div>

                <Select
                  label="Status"
                  placeholder="Any status"
                  value={filtersToApply.status ?? ''}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                    selectAppliedFilters(
                      'status',
                      event.target.value || undefined,
                    )
                  }
                  options={TRADE_STATUS_OPTIONS}
                />

                <Input
                  label="Pair"
                  placeholder="USD-NGN"
                  value={pair}
                  error={pairError}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    selectAppliedFilters(
                      'pair',
                      event.target.value.toUpperCase().trim() || undefined,
                    )
                  }
                  className="isFull"
                />

                <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-dark-borderMid">
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="cyan"
                    onClick={handleApply}
                    disabled={Boolean(pairError)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </Filter>

          {askId && (
            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-dark-inputBg dark:text-gray-200">
              Offer #{askId}
              <button
                type="button"
                aria-label={`Clear offer ${askId} filter`}
                onClick={clearAskId}
              >
                <XIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            </span>
          )}
        </div>

        <div className="flex w-full items-center gap-2 md:w-[460px]">
          {/* Bound to the applied search, so a tab switch or Clear keeps the
              box and the list in step. */}
          <SearchBar
            handleSearch={handleSearch}
            initialSearchTerm={appliedFilters.search ?? ''}
            clearFilters={() => handleSearch('')}
            placeholder="Search by trade ID or business name"
            dataCy="marketplace-trades"
          />
          <PermissionsProvider
            permission={AfriexAdminPermission.MarketPlaceRead}
          >
            <DownloadTradesCsvButton filters={appliedFilters} />
          </PermissionsProvider>
        </div>
      </div>

      {usesDefaultLookback && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Showing the last 30 days.
        </p>
      )}
    </div>
  );
};

export default TradesFilters;
